import { useState } from "react";
import { Link } from "react-router-dom";
import { useLang } from "@/lib/i18n";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import Navbar from "@/components/Navbar";
import { toast } from "sonner";
import { Mail, ArrowLeft, CheckCircle2 } from "lucide-react";

export default function ForgotPassword() {
  const { lang } = useLang();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <div className="max-w-md mx-auto px-6 py-16" data-testid="forgot-password-page">
        <Link to="/auth?mode=login" className="text-sm text-muted-foreground hover:text-primary inline-flex items-center gap-1 mb-6">
          <ArrowLeft className="w-3.5 h-3.5"/>{lang === "it" ? "Torna al login" : "Back to login"}
        </Link>

        {sent ? (
          <div className="text-center space-y-4" data-testid="forgot-sent-state">
            <div className="w-14 h-14 rounded-full bg-sage/15 grid place-items-center mx-auto">
              <CheckCircle2 className="w-6 h-6 text-sage"/>
            </div>
            <h1 className="font-display text-4xl tracking-tight">
              {lang === "it" ? "Controlla la tua email" : "Check your email"}
            </h1>
            <p className="text-sm text-muted-foreground">
              {lang === "it"
                ? `Se un account esiste per ${email}, ti abbiamo inviato un link per reimpostare la password. Il link è valido per 60 minuti.`
                : `If an account exists for ${email}, we've sent you a link to reset your password. The link is valid for 60 minutes.`}
            </p>
            <p className="text-xs text-muted-foreground pt-4">
              {lang === "it" ? "Non hai ricevuto niente? Controlla la cartella spam." : "Didn't receive anything? Check your spam folder."}
            </p>
            <Button variant="outline" onClick={() => setSent(false)} className="lift" data-testid="forgot-resend">
              {lang === "it" ? "Reinvia" : "Resend"}
            </Button>
          </div>
        ) : (
          <>
            <h1 className="font-display text-4xl tracking-tight mb-2" data-testid="forgot-title">
              {lang === "it" ? "Password dimenticata?" : "Forgot password?"}
            </h1>
            <p className="text-sm text-muted-foreground mb-6">
              {lang === "it"
                ? "Inserisci la tua email e ti manderemo un link per reimpostarla."
                : "Enter your email and we'll send you a link to reset it."}
            </p>
            <form onSubmit={submit} className="space-y-4" data-testid="forgot-form">
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email" type="email" required
                  value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  data-testid="forgot-input-email"
                />
              </div>
              <Button
                type="submit" disabled={loading}
                className="w-full bg-primary text-primary-foreground hover:bg-primary/90 lift"
                data-testid="forgot-submit"
              >
                <Mail className="w-4 h-4 mr-1"/>
                {loading ? "…" : (lang === "it" ? "Invia link di reset" : "Send reset link")}
              </Button>
            </form>
          </>
        )}
      </div>
    </div>
  );
}
